import { useState, useEffect } from "react";
import "./editstyles.css";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { updateEachSubUnitUrl } from "../../../../api/endpoints";
import { ButtonLoaderII } from "../../../../components/uxCollections/Loaders";
import { getAllData, saveData } from "../../../../tools/indexdb";
/**
 * EditSubUnitOverlay
 *
 * Props:
 *   subUnit   {object|null}  — the sub-unit being edited; null = closed
 *   index     {number}       — 0-based position in the sub-units array (for avatar colour/number)
 *   baseUnit  {string}       — name of the base unit (e.g. "Carton")
 *   onSave    (updated) => void
 *   onDelete  (id)      => void
 *   onClose   ()        => void
 */

const fmt = (n) => Number(n).toLocaleString();
const AVATAR_SHADES = ["#1E3A8A", "#1D4ED8", "#2563EB", "#3B82F6", "#60A5FA"];

export default function EditSubUnitOverlay({ subUnit, index, baseUnit, onSave, onDelete, onClose, currency, product, updateSelectedProduct, storeName }) {
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState({ name: "", price: "", perBase: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [loading, setloading] = useState(false);

  // Animate in when subUnit appears
  useEffect(() => {
    if (subUnit) {
      setForm({ name: subUnit.name, price: String(subUnit.price), perBase: String(subUnit.perBase) });
      setDeleteConfirm(false);
      const t = setTimeout(() => setVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [subUnit]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 320);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const price = Number(form.price);
    const perBase = Number(form.perBase);
    if (!name || isNaN(price) || price <= 0 || isNaN(perBase) || perBase <= 0) return;

    try {
      setloading(true);
      const url = `${updateEachSubUnitUrl}${product.storeID}/${product._id}/?type=update`;
      const payload = {
        employee: null,
        subUnit: {
          id: subUnit.id,
          name: form.name,
          price: form.price,
          perBase: form.perBase,
        },
      };
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.status === 404) {
        uiAlert(data.message, "error");
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update product.");
      }

      let { storeProductCollection, modifiedProduct } = data;
      updateSelectedProduct(modifiedProduct);

      let storeData = await getAllData(storeName);
      if (storeData) {
        storeData.storeProductCollection = storeProductCollection.products;

        await saveData(storeName, storeData);
      }

      onSave({
        ...subUnit,
        name,
        price,
        perBase,
        // recalculate qty to preserve base-unit equivalent
        qty: perBase * Math.round(subUnit.qty / subUnit.perBase),
      });
      uiAlert("Your sub unit has been updated", "success");
      handleClose();
      return;
    } catch (error) {
      console.log(error);

      uiAlert("Something went wrong, please try again", "error");
      return;
    } finally {
      setloading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }
    console.log("Deleting sub unit with ID:", subUnit);

    if (subUnit.qty) {
      if (!window.confirm(`There is ${subUnit.qty} ${subUnit.name} left in stcok, deleting will erase this stocks, do you want to continue?`)) {
        return;
      }
    }

    try {
      setloading(true);
      const url = `${updateEachSubUnitUrl}${product.storeID}/${product._id}/?type=delete`;
      const payload = {
        employee: null,
        subUnit: {
          id: subUnit.id,
          name: form.name,
          price: form.price,
          perBase: form.perBase,
        },
      };
      console.log("Deleting sub unit with payload:", payload);

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.status === 404) {
        uiAlert(data.message, "error");
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update product.");
      }

      let { storeProductCollection, modifiedProduct } = data;
      updateSelectedProduct(modifiedProduct);

      let storeData = await getAllData(storeName);
      if (storeData) {
        storeData.storeProductCollection = storeProductCollection.products;

        await saveData(storeName, storeData);
      }
      uiAlert("Your sub units have been deleted", "success");
      onDelete(subUnit.id);
      handleClose();
    } catch (error) {
      uiAlert("Something went wrong, please try again", "error");
    } finally {
      setloading(false);
    }
  };

  // Live conversion preview
  const preview = (() => {
    const pb = Number(form.perBase);
    if (!subUnit || isNaN(pb) || pb <= 0) return null;
    const baseEquiv = Math.round(subUnit.qty / subUnit.perBase);
    console.log(subUnit, Math.round(baseEquiv * pb), baseEquiv);

    return { qty: Math.round(baseEquiv * pb), baseEquiv };
  })();

  const avatarBg = AVATAR_SHADES[index % AVATAR_SHADES.length];

  // Always a centered modal — never a bottom sheet
  const sheetStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: visible ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -46%) scale(0.95)",
    opacity: visible ? 1 : 0,
    width: "min(440px, 92vw)",
    maxHeight: "90vh",
    borderRadius: 20,
    background: "#fff",
    zIndex: 1070,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    padding: "0 24px 28px",
    boxShadow: "0 32px 80px rgba(15,23,42,0.28), 0 0 0 1px rgba(0,0,0,0.06)",
    transition: "transform 0.32s cubic-bezier(0.32,0.72,0,1), opacity 0.22s ease",
  };

  if (!subUnit) return null;

  return (
    <>
      {/* Backdrop */}
      <div className=' sbo-backdrop--edit' onClick={handleClose} aria-hidden='true' />

      {/* Modal */}
      <div style={sheetStyle} role='dialog' aria-modal='true' aria-label={`Edit ${subUnit.name}`}>
        {/* Drag handle (visual only) */}
        <div className='sbo-handle' />

        {/* ── Header ── */}
        <div className='sbo-sh-header'>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className='sbo-sub-avatar' style={{ background: avatarBg, width: 38, height: 38, borderRadius: 10, fontSize: 15 }}>
              {index + 1}
            </div>
            <div>
              <p className='sbo-sh-label'>Edit Sub Unit</p>
              <p className='sbo-sh-title'>{subUnit.name}</p>
            </div>
          </div>
          <button className='sbo-sh-close' onClick={handleClose} aria-label='Close edit overlay'>
            <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
              <path d='M18 6L6 18M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* ── Form ── */}
        <div className='sbo-edit-form'>
          {/* Name */}
          <div className='sbo-field'>
            <label className='sbo-field-label'>Sub-unit name</label>
            <input
              className='sbo-field-input'
              type='text'
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='e.g. Box, Piece, Pack'
              autoFocus
            />
          </div>

          {/* Price */}
          <div className='sbo-field'>
            <label className='sbo-field-label'>Price per unit ({currency})</label>
            <div className='sbo-field-prefix-wrap'>
              <span className='sbo-field-prefix'>{currency}</span>
              <input
                className='sbo-field-input sbo-field-input--prefixed'
                type='text'
                inputMode='decimal'
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                placeholder='0'
              />
            </div>
          </div>

          {/* Quantity per base unit */}
          <div className='sbo-field'>
            <label className='sbo-field-label'>Quantity per {baseUnit}</label>
            <div className='sbo-field-prefix-wrap'>
              <input
                className='sbo-field-input sbo-field-input--suffixed'
                type='text'
                inputMode='numeric'
                value={form.perBase}
                onChange={(e) => setForm((f) => ({ ...f, perBase: e.target.value }))}
                placeholder='0'
              />
              <span className='sbo-field-suffix'>per {baseUnit}</span>
            </div>
          </div>

          {/* Live conversion preview */}
          {preview && form.name.trim() && (
            <div className='sbo-edit-preview'>
              <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='#2563EB' strokeWidth='2.5'>
                <path d='M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4' />
              </svg>
              <span>
                <strong>{fmt(form.perBase)}</strong> {form.name.trim()} &nbsp;= <strong>{1}</strong> {baseUnit} &nbsp;· {currency}{" "}
                {fmt(Number(form.price) || 0)} each
              </span>
            </div>
          )}
        </div>

        {/* ── Actions ── */}
        <div className='sbo-edit-actions'>
          <button disabled={loading} className='sbo-cta' onClick={handleSave}>
            {loading ? (
              <ButtonLoaderII color='#fff' />
            ) : (
              <>
                <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <path d='M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z' />
                  <polyline points='17 21 17 13 7 13 7 21' />
                  <polyline points='7 3 7 8 15 8' />
                </svg>
                Save changes
              </>
            )}
          </button>

          <button disabled={loading} className={`sbo-cta-delete${deleteConfirm ? " sbo-cta-delete--confirm" : ""}`} onClick={handleDelete}>
            {deleteConfirm ? (
              loading ? (
                <ButtonLoaderII color='#fff' />
              ) : (
                <>
                  <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' />
                    <line x1='12' y1='9' x2='12' y2='13' />
                    <line x1='12' y1='17' x2='12.01' y2='17' />
                  </svg>
                  Tap again to confirm delete
                </>
              )
            ) : (
              <>
                <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                  <polyline points='3 6 5 6 21 6' />
                  <path d='M19 6l-1 14H6L5 6' />
                  <path d='M10 11v6M14 11v6' />
                  <path d='M9 6V4h6v2' />
                </svg>
                Delete sub unit
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
